import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../SleepHub/sleepHubApi';
import { formatMoney } from './perksHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X,
  paletteAt
} from '../../component/common/designSystem';
import {
  SCHEME_STATUS_COLORS,
  SCHEME_TYPE_COLORS,
  ENROLLED_BY_COLORS,
  PLAN_MIX_COLORS,
  EXPIRY_RAMP,
  TENURE_RAMP,
  expiryChipClass,
  expiryLabel,
  schemeStatusChipClass
} from './perksTheme';

/**
 * Perks → Salary Sacrifice and Perks → Health Scheme.
 *
 * ONE COMPONENT, TWO PAGES. The two programmes are structurally identical —
 * a scheme catalogue plus an enrollment ledger, sharing the same status and
 * type enums — so they share a dashboard rather than a copy of one. The
 * differences arrive as props: the wording (`noun`), the accent colour, and
 * Health Scheme's extra cash-plan/occupational-plan split, which renders only
 * when the payload carries it.
 *
 * Read as four bands:
 *  1. Headline — what is on offer and who is on it.
 *  2. Catalogue — scheme status, provider type and expiry runway.
 *  3. Enrollment — reach, tenure, who did the enrolling, and the 30-day trend.
 *  4. The schemes themselves, with per-scheme headcount.
 */
const SchemeDashboard = ({
  fetcher,
  title,
  noun = 'scheme',
  accent = CHART_COLORS.brand,
  populationLabel = 'employees'
}) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const nounPlural = `${noun}s`;
  const statusSplit = stats?.statusSplit || [];
  const typeSplit = stats?.typeSplit || [];
  const expiryDistribution = stats?.expiryDistribution || [];
  const tenureDistribution = stats?.tenureDistribution || [];
  const enrolledBySplit = stats?.enrolledBySplit || [];
  const enrollmentsPerDay = stats?.enrollmentsPerDay || [];
  const perScheme = stats?.perScheme || [];
  const schemes = stats?.schemes || [];
  const planMix = stats?.planMix || null;

  const trendLabels = lastNDaysLabels(enrollmentsPerDay.length);

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'area' },
    colors: [accent],
    stroke: { curve: 'smooth', width: 2 },
    fill: AREA_FILL,
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { show: false },
    xaxis: { categories: trendLabels, tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} enrollments` } }
  };

  // Colour-coded per scheme so the bar chart and the table below read as the
  // same ranking.
  const perSchemeOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: perScheme.map((_scheme, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: perScheme.map((scheme) => scheme.name) },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} enrolled` } }
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{title} | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.catalog}
          title={`Active ${nounPlural[0].toUpperCase()}${nounPlural.slice(1)}`}
          value={stats ? formatBigNumber(stats.activeSchemes) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.users}
          title="People Enrolled"
          value={stats ? formatBigNumber(stats.enrolled) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Reach"
          value={stats ? `${stats.reach}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.seats}
          title="Seats Used"
          value={stats?.seatUtilisation != null ? `${stats.seatUtilisation}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.goals}
          title="Plans Offered"
          value={stats ? formatBigNumber(stats.totalPlans) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.rating}
          title="Annual Cost"
          value={stats ? formatMoney(stats.totalBilling) : '-'}
        />
      </div>

      {/* ---------- Catalogue ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 mt-6">
        <MiniDonutCard
          title={`${nounPlural[0].toUpperCase()}${nounPlural.slice(1)} Status`}
          subtitle="Where each one stands"
          data={statusSplit}
          colors={SCHEME_STATUS_COLORS}
          unit={nounPlural}
          legendLimit={4}
        />
        <MiniDonutCard
          title="Who Provides It"
          subtitle="Shoorah against your own"
          data={typeSplit}
          colors={SCHEME_TYPE_COLORS}
          unit={nounPlural}
          legendLimit={2}
        />
        <MiniDonutCard
          title="Expiry Runway"
          subtitle="How long before each lapses"
          data={expiryDistribution}
          colors={EXPIRY_RAMP}
          unit={nounPlural}
          legendLimit={5}
        />
        {/*
          Health Scheme only — it is the one programme that splits its plans
          into two families. Salary Sacrifice keeps a single flat plan list, so
          this card would be a donut with one slice and is left out there.
        */}
        {planMix && (
          <MiniDonutCard
            title="Plan Mix"
            subtitle="Cash plans against occupational health"
            data={planMix}
            colors={PLAN_MIX_COLORS}
            unit="plans"
            legendLimit={2}
          />
        )}
      </div>

      {/* ---------- Enrollment ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4 mt-6">
        <BigStatPanel
          title="Enrollment Reach"
          subtitle={`Share of ${populationLabel} on a ${noun}`}
          value={stats ? `${stats.reach}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.enrolled)} of ${formatBigNumber(
                stats.totalUsers
              )} ${populationLabel}`
              : null
          }
        />

        <MiniDonutCard
          title="How Long Enrolled"
          subtitle="Tenure on the current enrollment"
          data={tenureDistribution}
          colors={TENURE_RAMP}
          unit="enrollments"
          legendLimit={5}
        />

        {/*
          Belongs with the enrollment band rather than the catalogue: it is a
          fact about how people got onto a scheme, not about the scheme. Shown
          for both programmes — Health Scheme's extra Plan Mix card sits in the
          catalogue band above, so neither one displaces this.
        */}
        <MiniDonutCard
          title="Who Enrolled Them"
          subtitle="Added by Shoorah or by you"
          data={enrolledBySplit}
          colors={ENROLLED_BY_COLORS}
          unit="enrollments"
          legendLimit={2}
        />

        {/*
          Live against lapsed. An enrollment that was removed is churn, and it
          is the number that explains a headcount falling while the scheme list
          stays the same.
        */}
        <Panel title="Enrollment Health" subtitle="Live against lapsed">
          <ProgressList
            items={[
              { label: 'Live enrollments', count: stats?.activeEnrollments || 0 },
              { label: 'Lapsed or removed', count: stats?.lapsedEnrollments || 0 }
            ]}
            unit="enrollments"
          />
        </Panel>
      </div>

      {/* ---------- Trends and uptake ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="New Enrollments" subtitle="Per day — last 30 days">
          {enrollmentsPerDay.some((value) => value > 0) ? (
            <Chart
              type="area"
              height={340}
              width="100%"
              series={[{ name: 'Enrollments', data: enrollmentsPerDay }]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No new enrollments in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        <Panel title={`Uptake by ${noun[0].toUpperCase()}${noun.slice(1)}`} subtitle="People on each">
          {perScheme.some((scheme) => scheme.people > 0) ? (
            <Chart
              type="bar"
              height={340}
              width="100%"
              series={[{ name: 'Enrolled', data: perScheme.map((scheme) => scheme.people) }]}
              options={perSchemeOptions}
            />
          ) : (
            <PanelEmpty>Nobody is enrolled yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- The schemes ---------- */}
      <div className="mt-6">
        <Panel
          title={`${title} Catalogue`}
          subtitle={`Every ${noun} you offer, newest first`}
        >
          {schemes.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">{noun[0].toUpperCase() + noun.slice(1)}</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Provided by</th>
                  <th className="py-2 pr-4 font-medium text-right">Plans</th>
                  <th className="py-2 pr-4 font-medium text-right">Seats</th>
                  <th className="py-2 pr-4 font-medium text-right">Enrolled</th>
                  <th className="py-2 pr-4 font-medium text-right">Cost</th>
                  <th className="py-2 font-medium">Expiry</th>
                </>
              }
            >
              {schemes.map((scheme) => {
                // Headcount comes from the enrollment half, which only lists
                // schemes somebody is actually on — a scheme absent from it
                // has nobody, not an unknown number.
                const uptake = perScheme.find((row) => String(row.id) === String(scheme.id));
                return (
                  <tr key={scheme.id} className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">
                      <div className="font-medium truncate">{scheme.name}</div>
                      {scheme.provider && scheme.provider !== scheme.name && (
                        <div className="text-xs text-shoorah-gray3 truncate">
                          {scheme.provider}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${schemeStatusChipClass(
                          scheme.status
                        )}`}
                      >
                        {scheme.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4">{scheme.type || '-'}</td>
                    <td className="py-2 pr-4 text-right">{scheme.planCount}</td>
                    <td className="py-2 pr-4 text-right">
                      {scheme.seats ? formatBigNumber(scheme.seats) : '-'}
                    </td>
                    <td className="py-2 pr-4 text-right font-semibold">
                      {uptake ? formatBigNumber(uptake.people) : '-'}
                    </td>
                    <td className="py-2 pr-4 text-right">{formatMoney(scheme.billingAmount)}</td>
                    <td className="py-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${expiryChipClass(
                          scheme.daysToExpiry
                        )}`}
                      >
                        {expiryLabel(scheme.daysToExpiry)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </PanelTable>
          ) : (
            <PanelEmpty>No {nounPlural} have been set up for your company yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

SchemeDashboard.propTypes = {
  fetcher: PropTypes.func.isRequired,
  /** Page title, e.g. "Salary Sacrifice". */
  title: PropTypes.string.isRequired,
  /** Singular noun for one entry, e.g. "scheme". */
  noun: PropTypes.string,
  /** Accent colour for the trend chart, from the design system's tokens. */
  accent: PropTypes.string,
  populationLabel: PropTypes.string
};

export default SchemeDashboard;
